/**
 * 전투 결과 화면.
 *
 * 여기가 이 게임의 도박이 성립하는 자리다. 지금 철수하면 전과가 확정되고,
 * 더 들어가면 적이 강해지는 대신 전과가 쌓인다. 그러다 장수가 쓰러지면
 * 전과가 0이 되고 남은 병력은 퇴각해 빠져나온 병사만 살아 돌아온다. 그 계산이 한눈에 서야 한다.
 */
import { foeLeadersFor } from '../meta/foe-leaders.ts';
import { seasonTurn } from '../meta/season.ts';
import { seasonText } from './realm.ts';
import {
  BRANCH_NAMES,
  COMPANIONS,
  CULTURE_NAMES,
  MAX_DEPTH,
  TROOP_BY_ID,
  companionAtDepth,
  depthRoster,
  foeHeadSquad,
  foeSquadsAt,
  foeSubsPower,
  isGate,
  boostSpec,
  specForTroop,
} from '../sim/troops.ts';
import type { Culture } from '../sim/troops.ts';
import { lateFoes } from '../sim/scenario.ts';
import { SLOT_NAMES, equipOf } from '../sim/equipment.ts';
import {
  ROUTES,
  TRAIT_BY_ID,
  Trait,
  forecastTraits,
  gateStoryName,
  isRouteDepth,
  segmentDepths,
  traitNames,
  traitsForDepth,
} from '../sim/traits.ts';
import { supplyNames } from '../sim/supplies.ts';
import type { Army } from '../meta/army.ts';
import type { BattleOutcome, RunState } from '../meta/run.ts';
import {
  salaryDue,
  salaryFloor,
  currentSurvivalPct,
  earningCompanions,
  enemyCultureFor,
  heroLevelGains,
  remainingTroops,
  averageFatiguePct,
  routeOf,
  runTraits,
  scouted,
} from '../meta/run.ts';
import { FATIGUE_ATK_PCT, FATIGUE_SPEED_PCT } from '../sim/world.ts';
import {
  RAID_XP_CAP,
  heroLevelFor,
  SALARY_DEPTH,
  salaryDepthFor,
  salaryFor,
  heroLevelOf,
  heroXpOf,
  promotableCount,
  rankFor,
  squadSizeFor,
  startDepths,
} from '../meta/army.ts';
import type { LevelUp, Who } from '../meta/run.ts';
import { button, clear, el, objectJosa, subjectJosa } from './dom.ts';
import { TERRITORY_BY_ID } from '../meta/realm.ts';
import { itemTipButton } from './items.ts';

export interface ResultActions {
  readonly onAdvance: () => void;
  readonly onRetreat: () => void;
  readonly onReturn: () => void;
  /** 이 판을 다시 본다(`main.ts`의 `openReplay`). 기록이 없으면 없다 — 단추도 그리지 않는다. */
  readonly onReplay?: () => void;
}

/**
 * 적 명부의 단련(`boostSpec`)을 " · 단련 ×1.39"처럼(113부터 명부가 단련을 싣지 않아 늘 빈 글이다 — 되살리면 다시 보인다). 레벨은 99에서 멈추고 머릿수도 그대로라, 적어 두지 않으면 왜 더
 * 센지 화면에서 안 읽힌다(리뷰 제안). 단련이 없으면 빈 글.
 */
function boostText(roster: readonly { boost?: number }[]): string {
  const b = roster[0]?.boost;
  return b !== undefined && b > 100 ? ` · 단련 ×${(b / 100).toFixed(b >= 1000 ? 0 : 2)}` : '';
}

export function renderResult(
  root: HTMLElement,
  army: Army,
  run: RunState,
  outcome: BattleOutcome,
  actions: ResultActions,
): void {
  clear(root);

  const failed = run.failed;
  const left = remainingTroops(run);
  const nextDepth = run.depth + 1;
  // 미리보기는 실제로 만날 상대의 편성이어야 한다. 깊이마다 문화가 바뀐다.
  // 다음 깊이의 전장 특성(traits.ts). 편성과 머릿수를 바꾸므로 미리보기도 같은 특성으로 잰다 — 출정 중에는 편성을
  // 못 바꾸니, 진군하기 전에 무엇이 오는지 알아야 물러설지 고른다.
  // 관문을 넘으면 다음 깊이는 갈림길이다(`isRouteDepth`) — 길을 고르기 전에는 특성을 모른다. 그때는 길마다 구간 네
  // 깊이를 적고(`forkLines`), 적 수는 길에 따른 폭으로 적는다.
  const fork = isRouteDepth(nextDepth) && routeOf(run, nextDepth) === null;
  const nextTraits = runTraits(run, nextDepth);
  const nextCulture = enemyCultureFor(army.culture, nextDepth);
  const preview = depthRoster(nextCulture, nextDepth, nextTraits);
  // 다음 깊이를 이겨 본 적이 있나(`Army.deepest`). 이 판을 정산하며 이미 갱신됐다. 처음 가는 전장인지 알아야 모르는 곳에
  // 쌓은 전과를 걸지 고른다(플레이한 사람: "다음 전장에 대한 정보가 좀 더 나왔으면 좋겠어. 특히 깬 적이 없는 전장은
  // 알려주면 좋겠어").
  const nextFresh = nextDepth > army.deepest;
  const nextCount = preview.reduce((n, e) => n + e.count, 0);
  // 판 도중에 더 오는 적(매복·원군). 처음 수와 나눠 적는다.
  const late = lateFoes(preview, nextTraits);
  // 갈림길이면 길마다 다음 깊이의 적 수와 더 오는 적. "적 44–58명"처럼 폭으로 적는다. 더 오는 적도 길마다 잰다 — 빈
  // 특성(`nextTraits`)으로 재면 매복·원군이 늘 0으로 나와, 매복 16명이 오는 깊이 16을 "적 72명"으로만 적었다(리뷰가 짚었다).
  const forks = fork
    ? ROUTES.map((r) => {
        const traits = traitsForDepth(run.seed, nextDepth, r.id);
        const roster = depthRoster(nextCulture, nextDepth, traits);
        return { count: roster.reduce((n, e) => n + e.count, 0), late: lateFoes(roster, traits) };
      })
    : [];
  const countText = fork ? countRange(forks.map((f) => f.count)) : `${nextCount}`;
  // 안개(`Trait.Fog`): 척후가 보지 않았으면 다음 깊이의 특성·편성·더 오는 적을 가린다. 머릿수만 안다.
  const veiled = !fork && nextTraits.includes(Trait.Fog) && !scouted(run, nextDepth);
  const shownTraits = forecastTraits(nextTraits, !veiled);
  const ambushText = fork ? countRange(forks.map((f) => f.late.ambush)) : `${late.ambush}`;
  const reinforceText = fork ? countRange(forks.map((f) => f.late.reinforce)) : `${late.reinforce}`;
  const lateText = fork ? countRange(forks.map((f) => f.late.ambush + f.late.reinforce)) : `${late.ambush + late.reinforce}`;
  // 적은 깊이만큼의 레벨로 선다. 레벨 1로 재면 깊은 곳의 적이 실제보다 약해 보인다.
  // 명부에 단련이 실려 있으면 그 몫까지 센다(`boostSpec` — 113부터 적 명부는 싣지 않는다).
  // 부장의 격을 더한다(`foeSubsPower`) — 부장은 병사 값에서 치른 몫이라, 빼고 적으면 부장이 선 만큼 약해 보인다.
  const nextPower =
    preview.reduce((n, e) => n + boostSpec(specForTroop(e.troop, e.level), e.boost).power * e.count, 0) + foeSubsPower(nextCulture, nextDepth, nextTraits);
  // 철수하면 전과가 확정되고, 그 전과로 장수가 자란다. 오르는 레벨이 있으면 철수 쪽에 보인다.
  // 토벌 경험치도 한도까지 레벨에 든다. 전과만 보면 토벌을 많이 돈 장수의 레벨을 낮게 적는다.
  const heroNow = heroLevelOf(army);
  const rankNow = rankFor(army.totalMerit);
  const rankAfter = rankFor(army.totalMerit + outcome.meritIfRetreat);
  // 전투 성과 경험치(`deedXpIfRetreat`)도 철수해야 들어온다. 전과만 더하면 오를 레벨을 낮게 적는다.
  const heroAfter = heroLevelFor(heroXpOf(army) + outcome.meritIfRetreat + outcome.deedXpIfRetreat);
  // 토벌의 경험치는 이미 계정에 들어갔다. 오르기 전 레벨은 그 몫을 빼서 잰다.
  const ranking = rankDeeds(army, run, outcome);
  const heroBeforeRaid = heroLevelFor(
    army.totalMerit + Math.min(army.raidXp - outcome.raidXp, RAID_XP_CAP) + army.deedXp,
  );

  // 영토 공격(Todo9 4단계)이면 그 땅의 이름으로 적는다.
  const land = run.territory ? TERRITORY_BY_ID.get(run.territory.id) : undefined;
  const title = failed
    ? '장수가 쓰러졌다'
    : land
      ? outcome.captured
        ? `${land.name}${objectJosa(land.name)} 빼앗았다`
        : `${land.name}${objectJosa(land.name)} 빼앗지 못했다`
      : run.raid
      ? outcome.won
        ? '두령을 베었다'
        : '토벌에서 물러났다'
      : outcome.won
        ? `${run.depth}번째 전장을 쓸었다`
        : `${run.depth}번째 전장에서 물러섰다`;

  const lostItems = outcome.gearLost.flatMap((g) => g.items);
  // 잃지 않고 ★ 하나가 깎인 강화 물건(`GearLoss.worn`).
  const wornItems = outcome.gearLost.flatMap((g) => g.worn ?? []);
  const joinedDef = outcome.joined ? COMPANIONS.find((c) => c.id === outcome.joined) : undefined;
  // 진급은 출정 화면의 병영에서 한다. 여기서 알려주지 않으면 경험치가 찬 것을 모르고 지나친다.
  const promotable = promotableCount(army);
  // 토벌도 전멸도 아니어야 철수·진군을 고른다.
  // 영토 공격은 한 판으로 끝난다 — 철수·진군을 고르지 않는다(아래 막대의 "돌아간다" 하나).
  const choosing = !failed && !run.raid && !run.territory;
  const deepest = run.depth >= MAX_DEPTH;

  // ---- 한눈에: 숫자 칸 · 전리품 · 철수와 진군 · 자세히 ----
  //
  // 처음에는 요약을 "쓰러짐 · 이번 판 · 이번 출정 · 철수하면 · 다음 적" 다섯 줄 글로 모았다(그 전에는 더 아래로 흩어져
  // 있었다 — "가장 중요한 정보는 누가 죽었는가, 이번 판에서 얼마를 벌고 무엇을 얻었는가, 누적으로 현재까지 얻은 것, 다음
  // 적은 몇 명인가"). 줄마다 같은 크기의 글이라 무엇이 큰일인지 안 보였고, 전리품은 이름 한 조각이었다. 플레이한 사람이
  // 청했다: "전투 결과 화면 디자인을 개선해줘. 한 눈에 잘 들어오도록 말이야. 특히 전리품은 아이콘도 보여주고."
  // 그래서 맨 위에 큰 숫자 칸 넷(전과·노획·쓰러짐·처치), 그 아래 전리품 카드(아이콘), 철수와 진군을 두 카드로 나란히,
  // 나머지(쓰러진 이름·이번 출정 누계·그 밖)는 그 아래 작은 줄로 둔다.
  //
  // 그 뒤 **이번 판과 누계를 절로 갈랐다.** 맨 위 숫자 칸에 "쌓은 전과"(출정 누계)가 "노획 +194냥"(이번 판) 옆에 서고,
  // 전리품 카드에 앞 판 것이 섞이고, "이번 판"·"이번 출정" 줄이 한 칸에 붙어 있어 어느 수가 이 판 것인지 가려 읽어야
  // 했다(플레이한 사람: "결과 화면에 누적량과 이번전투에서 얻은량이 섞여있어. 섹션 잘나눠서 구분되게 해줘"). 이제
  // 절마다 제목을 달고, 이번 전투 → 이번 출정 누계 → 철수할까 진군할까 → 이번 전투의 전공 순으로 둔다. 이번 판의 수는
  // 모두 "+"로 적고, 누계는 붙이지 않는다.
  const fallenCount = outcome.fallenMen.length + outcome.scatteredMen.length;
  const downedNames = outcome.downedCompanions.map((id) => COMPANIONS.find((c) => c.id === id)?.name ?? id);
  const kpis = el(
    'div',
    { class: 'kpis' },
    run.raid
      ? kpi('장수 경험치', `+${outcome.raidXp}`, outcome.raidXp > 0 ? 'good' : '')
      : failed
        ? kpi('전과', '없음', 'bad')
        : kpi('전과', `+${outcome.battleMerit.toLocaleString()}`, 'gold'),
    // 장수가 쓰러졌으면 이 판의 노획은 0이고 쌓아 둔 노획이 사라졌다. "+0냥"만 크게 보이면 잃은 것이 없는 판처럼
    // 읽혔다(QA가 짚었다) — 사라진 냥을 빨갛게 적는다.
    !run.raid && failed
      ? kpi('노획', outcome.goldLost > 0 ? `−${outcome.goldLost.toLocaleString()}냥` : '없음', 'bad')
      : kpi('노획', `+${outcome.gold.toLocaleString()}냥`, 'gold'),
    // 동료가 쓰러진 판도 "없음"(초록)으로 크게 보이면 잃은 것이 없는 판처럼 읽혔다(리뷰가 짚었다). 병사가 없고 동료만
    // 쓰러졌으면 큰 값이 동료 수다.
    kpi(
      '쓰러짐',
      fallenCount > 0 ? `${fallenCount}명` : downedNames.length > 0 ? `부하장수 ${downedNames.length}` : '없음',
      fallenCount > 0 || downedNames.length > 0 ? 'bad' : 'good',
      // 큰 숫자는 잃은 병사만 센다. 다친 병사(부상)는 따로 적는다 — 마흔이 쓰러졌는데 "27명"만 보이면 열셋이 어디
      // 갔는지 모른다(QA가 짚었다).
      // 부상 글은 한 덩어리로 접히게 묶고(폭 360에서 "부상 6 / 명은"으로 끊겼다) 전공 표와 같은 황토색으로 적는다(QA).
      downedNames.length > 0 || outcome.woundedMen.length > 0
        ? el(
            'span',
            {},
            downedNames.length > 0 ? `부하장수 ${downedNames.join(', ')} 요양` : '',
            downedNames.length > 0 && outcome.woundedMen.length > 0 ? ' · ' : '',
            outcome.woundedMen.length > 0 ? el('span', { class: 'kn-hurt' }, `부상 ${outcome.woundedMen.length}명은 병영으로`) : null,
          )
        : '',
    ),
    kpi('처치한 적의 격', outcome.killScore.toLocaleString(), ''),
  );

  // 이번 판의 전리품과 잃은 장비만. 앞 판들에 주운 것은 누계 절에 있다.
  // 영토를 빼앗은 판의 특산도 이번 판에 얻은 것이다(`BattleOutcome.gift`).
  const loot = lootCard([...outcome.drops, outcome.gift].filter((x): x is string => !!x), lostItems);

  // 이번 판의 자세한 일: 이름과 그 밖의 일.
  const rows: HTMLElement[] = [];
  if (fallenCount > 0 || downedNames.length > 0) rows.push(fallenRow(outcome));
  // 부상(`WOUNDED_PCT`): 이긴 판에서 쓰러졌지만 죽지 않은 병사. 쓰러짐과 따로 적는다 — 잃은 사람이 아니다.
  if (outcome.woundedMen.length > 0) rows.push(woundedRow(outcome));
  // 이 판에 쓴 군수품(supplies.ts). 쓰면 하나 준다 — 무엇이 줄었는지 보인다.
  if (outcome.usedSupplies.length > 0) {
    rows.push(sumRow('군수품', supplyNames(outcome.usedSupplies).map((n): Part => [`${n} 씀`, ''])));
  }
  const now: Part[] = [];
  if (joinedDef) now.push([`${joinedDef.name} 합류`, 'good']);
  // 처음 깬 전장. 넘은 것이 관문이면 출발 깊이 지름길이 새로 열린다(`startDepths`) — 다음 출정 화면에서 고른다.
  if (outcome.firstClear) {
    now.push([`깊이 ${run.depth} 첫 승리`, 'good']);
    if (startDepths(army).includes(run.depth + 1)) now.push([`다음 출정부터 깊이 ${run.depth + 1}에서 시작할 수 있다`, 'good']);
  }
  if (run.raid && outcome.raidXp > 0) {
    now.push([raidXpText(army, outcome, heroNow, heroBeforeRaid), heroNow > heroBeforeRaid ? 'good' : '']);
  }
  if (outcome.leaderDeedXp > 0) now.push([`장수 성과 경험치 +${outcome.leaderDeedXp.toLocaleString()}(철수 때)`, 'good']);
  if (now.length > 0) rows.push(sumRow('그 밖에', now));

  // 이번 출정 누계. 토벌은 한 판으로 끝나 누계가 이번 판과 같으니 두지 않는다.
  let tally: HTMLElement | null = null;
  if (!run.raid && !run.territory) {
    const t = run.tally;
    const tallyRows: HTMLElement[] = [];
    if (t.joined.length > 0) {
      tallyRows.push(sumRow('합류', [[namesOf(t.joined, (id) => COMPANIONS.find((c) => c.id === id)?.name), 'good']]));
    }
    // 부상으로 병영에 돌아간 병사(이 출정의 합). 옛 저장의 출정에는 값이 없다.
    if ((t.hurt ?? 0) > 0) tallyRows.push(sumRow('부상', [[`병사 ${t.hurt}명 — 병영에서 쉰다`, 'warn']]));
    // 피로(Todo6): 남은 병사의 평균. 깊이를 넘어 쌓이므로 더 들어갈지를 가르는 수다(플레이한 사람: "결과 화면에 현재
    // 평균 피로도도 보여주면 좋겠다").
    // 줄 하나로 적었더니 묻혔다(플레이한 사람: "전투 후 피로도는 조금 더 강조해서 보여줘") — 누계 머리의 게이지로 올렸다
    // (`fatigueMeter`).
    const fat = averageFatiguePct(run);
    // 급료(Todo6): 떠날 때 먼저 낸 돈과 번 돈을 견준다 — 본전을 넘겼는지가 더 들어갈지를 가른다.
    if ((run.wage ?? 0) > 0) {
      // 장수가 쓰러졌으면 쌓아 둔 노획이 사라졌다 — 남은 것은 낸 급료뿐이다.
      const net = (failed ? 0 : t.gold) - (run.wage ?? 0);
      tallyRows.push(sumRow('급료', [[`−${(run.wage ?? 0).toLocaleString()}냥 · 순이익 ${net >= 0 ? '+' : '−'}${Math.abs(net).toLocaleString()}냥`, net >= 0 ? 'good' : 'bad']]));
    }
    // 쌓인 장수 성과 경험치는 여기 두지 않는다. 바로 아래 "철수하면" 카드가 같은 수를 적는다.
    if (t.loot.length > 0) {
      const items = t.loot.map((id) => equipOf(id)).filter((x): x is NonNullable<typeof x> => !!x);
      tallyRows.push(
        el(
          'div',
          { class: 'srow' },
          el('span', { class: 'sk' }, '주운 것'),
          el('span', { class: 'sv loot-icons' }, ...items.map((it) => itemTipButton(it, 32))),
        ),
      );
    }
    tally = el(
      'section',
      { class: 'rsec-body', 'aria-label': '이번 출정 누계' },
      el(
        'div',
        { class: 'kpis tally' },
        // 쌓은 전과는 늘 둔다. 아래 "철수하면" 카드와 같은 수라 한때 뺐더니, 누계에 전과가 없어 위 "이번 전투"의
        // 전과를 누계로 읽었다(플레이한 사람: "이번전투 전과는 혹시 누적인거 아니야? 이번 출정 누계에 전과는 없네").
        // 여기는 쌓인 상태, 카드는 철수하면 받는 것이라 뜻이 다르다. 장수가 쓰러지면 "사라짐" — 0으로 적으면 처음부터
        // 없던 것처럼 읽힌다.
        failed ? kpi('쌓은 전과', '사라짐', 'bad') : kpi('쌓은 전과', run.pendingMerit.toLocaleString(), 'gold'),
        // 출정의 노획은 철수해야 들어온다(`RunState.pendingGold`) — 전과처럼 "쌓은" 것이고, 장수가 쓰러지면 사라진다.
        failed ? kpi('쌓은 노획', '사라짐', 'bad') : kpi('쌓은 노획', `${outcome.goldIfRetreat.toLocaleString()}냥`, 'gold'),
        kpi('잃은 병사', `${t.lost}명`, t.lost > 0 ? 'bad' : ''),
        kpi('남은 병력', `${left}/${run.deployed}명`, ''),
      ),
      // 장수가 쓰러진 판에는 두지 않는다 — 출정이 끝나 피로가 더 쓰일 데가 없는데 붉은 게이지가 한 칸을 차지했다(QA).
      left > 0 && !failed ? fatigueMeter(fat, !deepest) : null,
      tallyRows.length > 0 ? el('div', { class: 'panel result-summary' }, ...tallyRows) : null,
    );
  }

  // 철수와 진군: 두 카드로 나란히. 아래 막대의 두 단추와 같은 순서다.
  let choices: HTMLElement | null = null;
  if (choosing) {
    // 확정될 전과는 카드 머리에 크게 있다. 줄로 또 적지 않는다.
    const back: Part[] = [[`생환 배율 ${currentSurvivalPct(run)}%`, '']];
    // 쌓아 둔 노획도 철수해야 들어온다(`RunState.pendingGold`) — 장수가 쓰러지면 사라진다.
    if (outcome.goldIfRetreat > 0) back.push([`노획 +${outcome.goldIfRetreat.toLocaleString()}냥 확정`, 'good']);
    // 월급(`salaryDue`): 철수해야 받는다 — 철수를 고를 까닭이 하나 더 는다. 얕은 곳만 돈 출정이면 왜 없는지를 적는다
    // (관등에 월급이 있는데 이 줄이 없으면 빠뜨린 것처럼 보인다). 토벌·영토 공격에는 아예 없다.
    const salary = salaryDue(army, run);
    const fullSalary = salaryFor(army.totalMerit + outcome.meritIfRetreat);
    // 제 관등의 온 몫을 받으려면 이겨야 하는 깊이: 최고 깊이의 문턱(`salaryFloor`)과 관등의 깊이(`SALARY_DEPTH`) 가운데 깊은 쪽.
    const need = Math.max(salaryFloor(army), salaryDepthFor(army.totalMerit + outcome.meritIfRetreat));
    if (!run.raid && !run.territory && fullSalary > 0) {
      if (salary >= fullSalary) {
        back.push([`월급 +${salary.toLocaleString()}냥(${rankAfter.name})`, 'good']);
      } else if (salary > 0) {
        // 얕은 곳에서 이겨 제 관등의 몫을 다 받지 못한다 — 얼마나 깊이 가야 다 받는지 적는다.
        back.push([`월급 +${salary.toLocaleString()}냥 — 깊이 ${need} 이상에서 이기면 ${rankAfter.name}의 ${fullSalary.toLocaleString()}냥`, '']);
      } else {
        // "10칸 안"이라고 적었더니 무엇의 열 칸인지 낯설었다(QA) — 이겨야 하는 깊이를 그대로 적는다. 얕은 곳에서라도
        // 받을 수 있는 가장 얕은 깊이다(비장의 깊이 — 그 아래로는 월급이 없다).
        back.push([`월급 없음 — 깊이 ${Math.max(salaryFloor(army), SALARY_DEPTH[1])} 이상에서 이긴 판이 있어야 나온다`, 'dim']);
      }
    }
    if (outcome.deedXpIfRetreat > 0) back.push([`장수 성과 경험치 +${outcome.deedXpIfRetreat.toLocaleString()}`, 'good']);
    if (heroAfter > heroNow) back.push([`장수 레벨 ${heroNow} → ${heroAfter}`, 'good']);
    // 함께 선 동료도 같은 전과를 받아 오른다(`retreat`과 같은 `earningCompanions`). 오를 사람만 적는다.
    for (const st of earningCompanions(army, run)) {
      const before = heroLevelFor(st.xp);
      const after = heroLevelFor(st.xp + outcome.meritIfRetreat + (run.pendingMateDeedXp[st.id] ?? 0));
      const name = COMPANIONS.find((c) => c.id === st.id)?.name ?? st.id;
      if (after > before) back.push([`${name} 레벨 ${before} → ${after}`, 'good']);
      // 동료도 제 관등이 오른다 — 그 동료가 이끄는 분대의 정원이 는다(`mateSquadSize`, 장수 정원이 천장).
      const gain = outcome.meritIfRetreat + (run.pendingMateDeedXp[st.id] ?? 0);
      const rankWas = rankFor(st.xp);
      const rankThen = rankFor(st.xp + gain);
      const sizeWas = Math.min(squadSizeFor(army.totalMerit), squadSizeFor(st.xp));
      const sizeThen = Math.min(squadSizeFor(army.totalMerit + outcome.meritIfRetreat), squadSizeFor(st.xp + gain));
      if (rankThen.name !== rankWas.name) {
        back.push([
          sizeThen > sizeWas
            ? `${name} 관등 ${rankWas.name} → ${rankThen.name} · 정원 ${sizeWas} → ${sizeThen}명`
            : `${name} 관등 ${rankWas.name} → ${rankThen.name} · 정원 ${sizeThen}명(장수의 정원까지)`,
          'good',
        ]);
      } else if (sizeThen > sizeWas) {
        // 동료의 관등은 그대로여도 장수의 관등이 올라 천장이 높아지면 정원이 는다(리뷰가 짚었다).
        back.push([`${name} 정원 ${sizeWas} → ${sizeThen}명(장수의 관등이 올라)`, 'good']);
      }
    }
    // 이번 출정에 서지 않은 동료도 장수의 관등이 올라 천장이 높아지면 정원이 는다 — 경험치는 받지 않으므로 관등은 그대로다.
    // 처음에는 함께 선 동료만 훑어 축하 장면과 어긋났다(리뷰가 짚었다).
    const earning = new Set(earningCompanions(army, run).map((st) => st.id));
    for (const st of army.companions) {
      if (earning.has(st.id)) continue;
      const sizeWas = Math.min(squadSizeFor(army.totalMerit), squadSizeFor(st.xp));
      const sizeThen = Math.min(squadSizeFor(army.totalMerit + outcome.meritIfRetreat), squadSizeFor(st.xp));
      if (sizeThen > sizeWas) {
        back.push([`${COMPANIONS.find((c) => c.id === st.id)?.name ?? st.id} 정원 ${sizeWas} → ${sizeThen}명(장수의 관등이 올라)`, 'good']);
      }
    }
    // 관등이 오르면 내 부대의 정원이 는다(동료 분대는 그 동료의 관등). 철수를 고를 까닭이 되므로 여기서 보인다.
    if (rankAfter.name !== rankNow.name) {
      back.push([
        `관등 ${rankNow.name} → ${rankAfter.name} · 내 부대 정원 ${squadSizeFor(army.totalMerit)} → ${squadSizeFor(army.totalMerit + outcome.meritIfRetreat)}명`,
        'good',
      ]);
    }

    let next: Part[];
    let nextBig = '';
    if (deepest) {
      // 가장 깊은 곳을 쓸었으면 더 들어갈 곳이 없다.
      // 조사를 숫자에 붙이지 않는다. "깊이 30가"로 나갔다(QA가 짚었다) — MAX_DEPTH가 바뀌면 또 틀린다.
      next = [[`없음 — 가장 깊은 곳(깊이 ${MAX_DEPTH})이다. 철수해 전과를 거둔다.`, '']];
    } else {
      const gate = isGate(nextDepth);
      nextBig = `적 ${countText}명`;
      next = [
        // 관문은 적이 한 번 크게 몰려오는 깊이다. 미리 알아야 여기서 물러설지 고른다.
        // 관문은 정해진 이야기 판이다(`GATE_STORIES`) — 이름을 붙여 미리 알린다.
        [`깊이 ${nextDepth}${gate ? ` · 관문 — ${gateStoryName(nextDepth) ?? ''}` : ''}`, gate ? 'warn' : ''],
        nextFresh ? [newTag('아직 깬 적 없는 전장'), 'new'] : [`이미 깬 전장 · 최고 깊이 ${army.deepest}`, 'dim'],
        // 누구와 싸우나: 나라와 레벨, 병종별 머릿수. 머릿수만으로는 창이 많은지 기병이 많은지 몰라 진군할지 못 고른다.
        ...(fork
          ? forkLines(run, nextDepth, CULTURE_NAMES[nextCulture])
          : veiled
            ? ([[`${CULTURE_NAMES[nextCulture]}군 · 안개에 가려 편성을 모른다`, '']] as Part[])
            : ([
                [`${CULTURE_NAMES[nextCulture]}군 · 레벨 ${enemyLevels(preview)}${boostText(preview)} · 전투력 ${nextPower.toLocaleString()}`, ''],
                [makeupDetails(enemyMakeup(preview, nextCulture)), 'dim'],
                // 누가 이끄는가(`meta/foe-leaders.ts`): 총대장과 부장들. 부대 수는 전장이 세우는 그 수다.
                [foeLeadLine(nextCulture, foeSquadsAt(nextCulture, nextDepth, nextTraits), nextTraits.includes(Trait.Warlords), nextDepth), 'dim'],
              ] as Part[])),
      ];
      if (nextFresh && army.deepest > 0) next.splice(2, 0, [`지금까지 최고 깊이 ${army.deepest}`, 'dim']);
      // 지친 채로 더 들어가는 값을 진군 쪽에도 적는다 — 고르는 자리에서 보여야 한다.
      const tired = fatigueLevel(averageFatiguePct(run));
      if (!run.raid && !run.territory && tired.cls !== '') {
        const f = averageFatiguePct(run);
        next.push([`병사 평균 피로 ${f}%(${tired.word}) — 치는 힘 약 −${fatigueAtkLoss(f)}% · 걸음 약 −${fatigueSpeedLoss(f)}%로 싸운다`, tired.cls]);
      }
      if (!veiled && ambushText !== '0') next.push([`매복 ${ambushText}명이 더 — 10초 뒤 우리 뒤에서`, 'warn']);
      if (!veiled && reinforceText !== '0') next.push([`원군 최대 ${reinforceText}명 — 20초마다, 그 전에 이기면 오지 않는다`, 'warn']);
      const opens = opensOnClear(army, nextDepth);
      if (opens.length > 0) next.push([`이기면 다음 출정부터 깊이 ${opens.join(', ')}에서 시작할 수 있다`, 'good']);
      if (gate) {
        next.push([
          companionAtDepth(army.culture, nextDepth, army.companions.length)
            ? '넘으면 새 부하장수가 합류한다'
            : '넘으면 다음 깊이는 숨을 돌린다',
          'warn',
        ]);
      }
      // 전장 특성: 이름·전과 가산·답. 그다음 깊이도 이름만 미리(예보는 두 깊이).
      // 안개에 가렸으면 안개만(가산도 가려 "?"). 척후가 보았으면 다 보인다.
      for (const t of shownTraits) {
        const def = TRAIT_BY_ID.get(t);
        if (def) next.push([veiled ? `${def.name} 전과 +?% — ${def.hint}` : `${def.name} 전과 +${def.meritPct}% — ${def.hint}`, 'trait']);
      }
      // 갈림길이면 위 길 줄이 이미 구간 전체를 적었다. 그다음 깊이가 새 갈림길이면(관문 앞) 그렇다고만 적는다. 척후를 보냈으면
      // 본 데까지 한 줄씩 더 적는다(`scoutTo`).
      const lastShown = Math.min(MAX_DEPTH, Math.max(nextDepth + 1, run.scoutTo ?? 0));
      for (let d = nextDepth + 1; d <= lastShown && !fork; d++) {
        if (isRouteDepth(d) && routeOf(run, d) === null) {
          next.push([`그다음 깊이 ${d}: 갈림길`, 'dim']);
          break;
        }
        const later = traitNames(forecastTraits(runTraits(run, d), scouted(run, d)));
        if (later.length > 0) next.push([`${d === nextDepth + 1 ? '그다음 ' : '척후 — '}깊이 ${d}: ${later.join(' · ')}`, 'dim']);
      }
    }
    choices = el(
      'div',
      { class: 'choices' },
      choiceCard('retreat', '철수하면', `전과 ${outcome.meritIfRetreat.toLocaleString()} 확정`, back),
      choiceCard('advance', '진군하면', nextBig, next),
    );
  }

  root.append(
    el(
      'div',
      { class: 'result-wrap' },
      el(
        'header',
        { class: 'result-head' },
        el(
          'span',
          { class: 'eyebrow' },
          // 토벌에는 깊이도 연전도 없다. 한 판으로 끝나는 싸움에 "깊이 1 ·
          // 1번째 전투"를 붙이면 런의 일부처럼 읽힌다.
          land ? `영토전 · ${land.name} ${land.hanja}` : run.raid ? '단신 토벌' : `깊이 ${run.depth} · ${run.battles}번째 전투`,
        ),
        el('h1', { class: failed ? 'fail' : '' }, title),
        // 이 판을 다시 본다 — 부대마다 따라가 보고, 느리게·빠르게 돌린다(`ui/replay-view.ts`).
        actions.onReplay ? button('▶ 전투 다시 보기', actions.onReplay, 'replay-open') : null,
      ),
      secHead('이번 전투', '이 판에서 얻고 잃은 것', 'now'),
      kpis,
      loot,
      rows.length > 0 ? el('section', { class: 'panel result-summary', 'aria-label': '이번 전투 자세히' }, ...rows) : null,
      run.raid && !failed
        ? el(
            'div',
            { class: 'panel' },
            el(
              'p',
              {},
              outcome.won
                ? '두령이 쓰러지자 나머지는 흩어졌다. 현상금이 붙는다.'
                : outcome.gold > 0
                  ? '두령을 베지 못하고 물러났다. 잡은 만큼은 값이 된다 — 현상금은 없다.'
                  : '두령을 베지 못하고 물러났다. 잡은 것이 없어 번 돈도 없다 — 잃은 것도 없다.',
            ),
            el('p', { class: 'dim' }, '병사를 걸지 않은 싸움이라 전과는 남지 않는다.'),
          )
        : failed && run.raid
        ? el(
            'div',
            { class: 'panel danger' },
            // 토벌은 병사를 데려가지 않는다. 여기에 일반 전멸 문구를 띄우면
            // 있지도 않은 부대를 잃었다고 말하는 것이 된다.
            el('p', {}, '도적의 손에 쓰러졌다. 노획은 없다.'),
            el('p', { class: 'dim' }, '병사를 걸지 않은 싸움이라 잃은 것도 없다. 다시 나가면 된다.'),
          )
        : failed
        ? el(
            'div',
            { class: 'panel danger' },
            // 장수가 쓰러지면 남은 병력이 후퇴하고, 아래로 빠져나간 병사는 살아 돌아온다(`World.leaderFall`).
            el(
              'p',
              {},
              outcome.survivors > 0
                ? `장수가 쓰러지자 남은 병력이 퇴각했다. ${outcome.survivors}명이 무사히 빠져나와 살아 돌아왔다. 이번 출정의 전과는 기록되지 않는다.`
                : '장수가 쓰러지자 남은 병력이 퇴각했지만 아무도 빠져나오지 못했다. 이번 출정의 전과는 기록되지 않는다.',
            ),
            // 쌓아 둔 노획이 사라졌다(`RunState.pendingGold`).
            outcome.goldLost > 0 ? el('p', {}, `쌓아 둔 노획 ${outcome.goldLost.toLocaleString()}냥도 사라졌다.`) : null,
            lostItems.length > 0
              ? el(
                  'p',
                  {},
                  // 조사는 마지막 칸 이름의 받침으로 — "방패을"로 나왔다(QA가 짚었다).
                  `차고 있던 ${lostSlotText(outcome)}${objectJosa(lostSlotText(outcome))} 잃었다. 창고에 두고 간 것은 무사하다.`,
                )
              : wornItems.length > 0
                ? null
                : el('p', { class: 'dim' }, '차고 있던 장비는 무사히 남았다.'),
            // 강화한 물건은 잃지 않고 ★ 하나가 깎였다(`wearDown`). 잃은 것과 따로 적는다 — 남은 물건이다.
            wornItems.length > 0
              ? el(
                  'p',
                  {},
                  `강화한 ${wornItems.map((w) => `${equipOf(w.from)?.name ?? w.from} → ★${equipOf(w.to)?.star ?? 0}`).join(', ')} — 잃지 않고 ★ 하나가 깎였다.`,
                )
              : null,
            el('p', { class: 'dim' }, '계정에 남은 병력과 군자금은 그대로다. 다시 꾸려서 나가면 된다.'),
          )
        : null,
      // 영토 공격의 결말: 빼앗았는지, 전과가 어디에 드는지.
      land && !failed
        ? el(
            'div',
            { class: `panel${outcome.captured ? ' joined' : ''}` },
            el(
              'p',
              {},
              outcome.captured
                ? `${land.name}${subjectJosa(land.name)} 이제 ${CULTURE_NAMES[army.culture]} 땅이다.`
                : `수비대를 다 물리치지 못했다. ${land.name}${subjectJosa(land.name)} 그대로 ${CULTURE_NAMES[run.territory!.owner]} 땅이다.`,
            ),
            el(
              'p',
              { class: 'dim' },
              `돌아가면 전과 ${outcome.meritIfRetreat.toLocaleString()}이 관등과 장수·부하장수의 성장에 든다. 최고 전과(순위)는 출정의 것이라 들지 않는다.`,
            ),
          )
        : null,
      // 이 판의 차례로 시즌이 끝났으면 지도는 이미 처음으로 돌아갔다 — 빼앗은 땅도. 장수가 쓰러진 판도 차례가 돌므로 이긴
      // 판과 따로 둔다(처음에는 이긴 판의 칸 안에 있어 전멸로 끝난 시즌은 알리지 않았다 — 리뷰가 짚었다).
      land && army.realm && army.realm.history?.length && seasonTurn(army.realm) === 0
        ? el(
            'div',
            { class: 'panel' },
            el('p', { class: 'warn' }, `이 판으로 시즌이 끝났다 — ${seasonText(army.realm.history[army.realm.history.length - 1])}. 지도는 처음으로 돌아갔다.`),
          )
        : null,
      // 사람이 합류하는 것은 줄 하나로 지나갈 일이 아니다. 이름을 가진 개체가
      // 오는 것은 전리품과 다르다.
      joinedDef
        ? el(
            'div',
            { class: 'panel joined' },
            el('h3', {}, `${joinedDef.name}${subjectJosa(joinedDef.name)} 합류했다`),
            el('p', { class: 'dim' }, `${joinedDef.title} — ${joinedDef.note}`),
            el('p', {}, '출정 화면에서 분대 하나를 맡길 수 있다.'),
          )
        : null,
      // 요약이 이름을 다 불렀으면 같은 명단을 또 두지 않는다. 요약이 "외 N명"으로 줄였거나, 전멸해
      // 쓰러진 병사와 흩어진 병사를 갈라 보여야 할 때만 둔다(요약은 둘을 한 줄로 센다).
      ...(fallenListed(outcome)
        ? []
        : [fallenPanel('쓰러진 병사', outcome.fallenMen), fallenPanel('퇴각하지 못하고 잃은 병사', outcome.scatteredMen)]),
      tally && secHead('이번 출정 누계', `${run.battles}번의 전투를 합친 것`, 'acc'),
      tally,
      choices && secHead('철수할까, 진군할까', '', 'next'),
      choices,
      // 판 사이에 체력·사기는 이어지지 않는다(명부에는 누가 남았는지만 실린다). 알려야 다친 병사를
      // 데리고 더 들어가도 되는지 고민하지 않는다(플레이한 사람이 물었다).
      choosing &&
        !deepest &&
        el('p', { class: 'dim' }, '진군하면 살아남은 병사는 체력과 사기를 모두 되찾고 나선다. 쓰러진 병사는 돌아오지 않는다 — 이긴 판에서는 열에 셋쯤이 다쳐서 병영으로 돌아간다.'),
      secHead('이번 전투의 전공', '누가 얼마나 베고 얼마나 자랐나', 'now'),
      el(
        'div',
        { class: 'panel result-stats' },
        stat('병사 경험치', soldierXpText(outcome)),
        // 진급은 출정 화면의 병영에서 한다. 여기서 알려주지 않으면 경험치가 찬 것을 모르고 지나친다. 이번 판에 나간
        // 사람만이 아니라 병영 전체의 수라 "병영에서"를 붙인다 — 이번 전투 절 안이라 이 판 성과로 읽혔다(리뷰가 짚었다).
        promotable > 0 ? stat('병영에서 진급 가능', `${promotable}명`, 'good') : null,
      ),
      // 병사별 전공은 맨 아래. 병사 수만큼 길어지는 칸이라 중간에 두면 레벨·처치 격이 그 뒤로 밀려
      // 내려가야 보였다(플레이한 사람이 청했다).
      deedsPanel(ranking, outcome),
      // 레벨이 오른 병사는 순위가 "Lv 1 → 2▲"로 이미 부른다. 순위 밖(열 명 아래)의 사람만 따로 적는다 — 처음에는
      // 순위 위에 모두를 또 적어, 같은 이름·같은 레벨이 한 화면에 두 번 나왔다.
      levelUpPanel(levelUpsOutsideRanking(ranking, outcome.levelUps), ranking !== null),
    ),
    actionBar(run, outcome, actions, {
      left,
      deepest,
      nextDepth,
      nextFresh,
      nextCount: countText,
      lateCount: veiled ? '0' : lateText,
      nextTraits: fork ? ['갈림길'] : traitNames(shownTraits),
    }),
  );
}

/** 적 수의 폭. 같으면 하나만. */
function countRange(counts: readonly number[]): string {
  const lo = Math.min(...counts);
  const hi = Math.max(...counts);
  return lo === hi ? `${lo}` : `${lo}–${hi}`;
}

/**
 * 갈림길 예보: 길마다 구간 네 깊이의 특성을 한 줄로("산골 길 — 6 산림 · 7 궁수대 · 8 좁은 협곡 · 9 비"). 진군하면 판에
 * 들기 전에 고른다(ui/crossroads.ts). 여기서 미리 보여야 물러설지 고른다.
 */
function forkLines(run: RunState, depth: number, culture: string): Part[] {
  const out: Part[] = [[`갈림길 — ${culture}군. 나서기 전에 길을 고른다`, 'warn']];
  for (const r of ROUTES) {
    const days = segmentDepths(depth).map((d) => {
      const names = traitNames(forecastTraits(traitsForDepth(run.seed, d, r.id), scouted(run, d)));
      return `${d} ${names.length > 0 ? names.join('·') : '특성 없음'}`;
    });
    out.push([`${r.name} — ${days.join(' / ')}`, 'trait']);
  }
  return out;
}

/** 절 제목. `now`는 이번 전투, `acc`는 출정 누계, `next`는 고를 일 — 색 띠로 가른다. */
function secHead(title: string, note: string, kind: 'now' | 'acc' | 'next'): HTMLElement {
  return el(
    'h2',
    { class: `rsec ${kind}` },
    el('span', { class: 'rsec-t' }, title),
    note ? el('span', { class: 'rsec-n' }, note) : null,
  );
}

/** 맨 위 숫자 칸 하나: 큰 값과 작은 이름(과 그 아래 한 줄). */
function kpi(label: string, value: string, cls: string, note: string | HTMLElement = ''): HTMLElement {
  return el(
    'div',
    { class: `kpi ${cls}` },
    el('span', { class: 'kv' }, value),
    el('span', { class: 'kk' }, label),
    note ? el('span', { class: 'kn' }, note) : null,
  );
}

/**
 * 전리품 카드. 이번 판에 주운 것은 큰 아이콘·이름·티어로, 앞 판들에 주운 것은 작은 아이콘 줄로, 전멸로 사라진 장비는
 * 붉게. 셋 다 없으면 카드가 없다. 처음에는 "전리품 환두대도(T2)" 글 한 조각이라 요약 줄에 묻혔다.
 */
function lootCard(now: readonly string[], lost: readonly string[]): HTMLElement | null {
  if (now.length === 0 && lost.length === 0) return null;
  const items = (ids: readonly string[]) =>
    ids.map((id) => equipOf(id)).filter((x): x is NonNullable<typeof x> => !!x);
  const card = el('section', { class: 'panel loot-card', 'aria-label': '전리품' });
  for (const item of items(now)) {
    card.append(
      el(
        'div',
        { class: 'loot-now' },
        itemTipButton(item, 56),
        el(
          'div',
          { class: 'loot-txt' },
          el('span', { class: 'kk' }, '전리품'),
          el('b', {}, item.name, ' ', el('span', { class: `tier t${item.tier}` }, `T${item.tier}`)),
          el('span', { class: 'dim' }, `${SLOT_NAMES[item.slot]} · 창고에 넣었다`),
        ),
      ),
    );
  }
  if (lost.length > 0) {
    card.append(
      el('div', { class: 'loot-row lost' }, el('span', { class: 'kk' }, '사라진 장비'), ...items(lost).map((it) => itemTipButton(it, 32))),
    );
  }
  return card;
}

/** 철수·진군 카드. 머리에 큰 값 하나, 아래에 줄들. */
function choiceCard(kind: string, title: string, big: string, parts: readonly Part[]): HTMLElement {
  return el(
    'section',
    { class: `panel choice ${kind}` },
    el('h3', {}, title, big ? el('span', { class: 'cbig' }, big) : null),
    el('ul', {}, ...parts.map(([text, c]) => el('li', { class: c }, text))),
  );
}

/** "처음" 꼬리표를 앞에 단 한 줄. 진군 카드에서 처음 가는 전장을 다른 줄과 가른다. */
function newTag(text: string): HTMLElement {
  return el('span', {}, el('span', { class: 'new-tag' }, '처음'), text);
}

/** 다음 적의 레벨. 한 깊이의 적은 모두 같은 레벨이지만, 섞이는 날을 위해 다르면 범위로 적는다. */
function enemyLevels(preview: readonly { readonly level: number }[]): string {
  const lv = preview.map((e) => e.level);
  const lo = Math.min(...lv);
  const hi = Math.max(...lv);
  return lo === hi ? `${lo}` : `${lo}~${hi}`;
}

/**
 * 다음 적의 머릿수. `byBranch`는 가지별 합계("민병 1 · 창 9 · 도 8 · 궁 5 · 기 1") — 한눈에 읽는 줄이다. `byTroop`는
 * 병종별("민병 1 · 창수 4 · 방패 창병 4 · 흑치 창병 1 …", 편성 순서) — 가지 합계만 적었더니 창 9 안에 창수·방패
 * 창병·흑치 창병이 섞여 정예가 몇인지 알 수 없었다(리뷰가 짚었다). 나라는 윗줄에 있으니 병종 이름 앞의 나라 이름은 뺀다.
 */
export function enemyMakeup(
  preview: readonly { readonly troop: string; readonly count: number }[],
  culture: Culture,
): { byBranch: string; byTroop: string } {
  const branch = new Map<string, number>();
  const troops: string[] = [];
  const prefix = `${CULTURE_NAMES[culture]} `;
  for (const e of preview) {
    const t = TROOP_BY_ID.get(e.troop);
    if (!t || e.count <= 0) continue;
    const name = BRANCH_NAMES[t.branch];
    branch.set(name, (branch.get(name) ?? 0) + e.count);
    troops.push(`${t.name.startsWith(prefix) ? t.name.slice(prefix.length) : t.name} ${e.count}`);
  }
  return { byBranch: [...branch].map(([n, c]) => `${n} ${c}`).join(' · '), byTroop: troops.join(' · ') };
}

/** "적장 계백 · 부장 흑치상지·성충" — 다음 판의 적을 누가 이끄는가. */
export function foeLeadLine(culture: Culture, squads: number, warlords = false, depth?: number): string {
  // 적장 결전은 0·1·2번 부대의 대장이 적장이고 나머지가 부장이다(`foeHeadSquad` — 118 전에는 셋뿐이라 모두 적장이었다).
  // 관문이 아닌 깊이에는 덜 알려진 장수들이 선다 — 예고도 전장과 같은 사람을 적는다.
  const all = foeLeadersFor(culture, squads, depth);
  const heads = all.filter((_, sq) => foeHeadSquad(sq, warlords));
  const subs = all.filter((_, sq) => !foeHeadSquad(sq, warlords));
  return `적장 ${heads.map((x) => x.name).join('·')}${subs.length > 0 ? ` · 부장 ${subs.map((x) => x.name).join('·')}` : ''}`;
}

/** 머릿수 줄: 가지별 합계를 누르면 병종별이 펼쳐진다. 좁은 폰에서 병종별을 늘 펼치면 카드가 여섯 줄 는다. */
function makeupDetails(m: { byBranch: string; byTroop: string }): HTMLElement {
  return el('details', { class: 'makeup' }, el('summary', {}, m.byBranch), el('span', {}, m.byTroop));
}

/**
 * 이 깊이를 처음 이기면 새로 열리는 출발 깊이(처음 넘는 관문이면 그 다음 깊이). 이미 이겨 본 깊이거나 관문이 아니면 빈
 * 배열이다. 규칙은 `startDepths` 하나에 둔다 — 여기서 관문 셈을 다시 적으면 그쪽을 고친 날 예고가 어긋난다.
 */
export function opensOnClear(army: Army, depth: number): number[] {
  if (depth <= army.deepest) return [];
  const now = startDepths(army);
  return startDepths({ ...army, deepest: depth }).filter((d) => !now.includes(d));
}

/** 병사 경험치: 살아 돌아온 몫과, 전투 성과로 가장 많이 더 받은 몫. */
function soldierXpText(outcome: BattleOutcome): string {
  if (outcome.survivors === 0 || outcome.xpEach === 0) return '없음';
  // 기본 몫과, 잘 싸운 만큼 더 받는 몫의 한도. 병사마다 받은 총량은 전공 순위에 있다. 처음에는 "생존자 +28 · 성과
  // 최대 +10"(이번 판에 가장 많이 받은 성과 몫)으로 적어 무슨 뜻인지 물었다.
  return `살아 돌아오면 +${outcome.xpEach}, 잘 싸운 만큼 +${outcome.xpEach}까지 더`;
}

/** 요약 한 줄의 조각: [글, 색 갈래]. */
type Part = readonly [string | HTMLElement, string];

/** 피로가 깎는 몫(%). 판정식(`World.fatigued`·`wearySpeed`)과 같은 비 — 가득 차면 `FATIGUE_ATK_PCT`·`FATIGUE_SPEED_PCT`. */
export function fatigueAtkLoss(pct: number): number {
  return Math.round((pct * FATIGUE_ATK_PCT) / 100);
}
export function fatigueSpeedLoss(pct: number): number {
  return Math.round((pct * FATIGUE_SPEED_PCT) / 100);
}

/**
 * 피로의 단계: 30% 아래는 가뿐하고, 60%부터는 크게 지쳤다. 색만으로 가르지 않게 말을 붙인다.
 * 문턱은 깎이는 몫으로 잡았다 — 30%면 치는 힘 −9%, 60%면 −18%(걸음 −12%)다.
 */
export function fatigueLevel(pct: number): { word: string; cls: '' | 'warn' | 'bad' } {
  if (pct >= 60) return { word: '크게 지침', cls: 'bad' };
  if (pct >= 30) return { word: '지침', cls: 'warn' };
  return { word: '가뿐함', cls: '' };
}

/**
 * 게이지 아래의 글. 깎이는 몫은 평균 피로로 낸 어림이라 "약"을 붙인다(병사마다 다르다). 반올림하면 0이 되는 쪽은
 * "−0%"라고 적지 않는다(QA). 더 들어갈 수 없는 판(가장 깊은 곳)에서는 "더 들어가면"을 말하지 않는다.
 */
export function fatigueNote(pct: number, canAdvance: boolean): string {
  if (pct <= 0) return '지치지 않았다.';
  const atk = fatigueAtkLoss(pct);
  const speed = fatigueSpeedLoss(pct);
  const tail = canAdvance ? '더 들어가면 이대로 싸운다. 철수하면 풀린다.' : '철수하면 풀린다.';
  // 반올림해 0이 되는 쪽은 적지 않는다. 처음에는 둘 다 0일 때만 걸러, 2%에서 "걸음 약 −0%"가 남았다(QA). 판정이 0이라는
  // 뜻은 아니다 — 1%도 아주 조금은 깎는다(리뷰) — 그래서 "적을 만큼 깎이지는 않는다"고 한다.
  const parts: string[] = [];
  if (atk > 0) parts.push(`치는 힘 약 −${atk}%`);
  if (speed > 0) parts.push(`걸음 약 −${speed}%`);
  if (parts.length === 0) return `아직 적을 만큼 깎이지는 않는다 — ${tail}`;
  return `${parts.join(' · ')} — ${tail}`;
}

/**
 * 결과 화면의 피로 게이지: 큰 숫자, 단계의 말, 막대, 깎이는 몫. 피로는 깊이를 넘어 쌓여 "더 들어갈지"를 가르는 수인데
 * 누계의 글 한 줄이라 눈에 띄지 않았다(플레이한 사람: "전투 후 피로도는 조금 더 강조해서 보여줘").
 */
export function fatigueMeter(pct: number, canAdvance = true): HTMLElement {
  const level = fatigueLevel(pct);
  const fill = el('span', { class: 'fat-fill' });
  fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  const bar = el('div', { class: 'fat-bar', role: 'img', 'aria-label': `병사 평균 피로 ${pct}퍼센트, ${level.word}` }, fill);
  return el(
    'div',
    { class: `fat-meter ${level.cls}` },
    el(
      'div',
      { class: 'fat-head' },
      // 남은 병사의 평균이다 — 싸우지 않은 병사와 가득 지친 병사가 섞여 있다(리뷰가 짚었다: "평균"이 빠지면 모두가
      // 이만큼 지친 것으로 읽힌다).
      el('span', { class: 'fat-k' }, '병사 평균 피로'),
      el('span', { class: 'fat-v' }, `${pct}%`),
      el('span', { class: 'fat-word' }, level.word),
    ),
    bar,
    el(
      'div',
      { class: 'fat-note' },
      fatigueNote(pct, canAdvance),
    ),
  );
}

function sumRow(label: string, parts: readonly Part[], cls = ''): HTMLElement {
  return el(
    'div',
    { class: `srow ${cls}` },
    el('span', { class: 'sk' }, label),
    // 조각 사이 " · "는 글로 둔다. CSS 간격만으로 나누면 화면 읽기에서 경계가 사라진다(쓰러진 병사
    // 목록에서 한 번 틀렸다).
    el(
      'span',
      { class: 'sv' },
      ...parts.flatMap(([text, c], k) => [k > 0 ? ' · ' : '', el('span', { class: c }, text)]),
    ),
  );
}

/** 요약 첫 줄: 누가 쓰러졌나. 이름을 부른다 — 숫자만이면 누가 쓰러졌는지가 아니라 재건 비용이 된다. */
function fallenRow(outcome: BattleOutcome): HTMLElement {
  const men = [...outcome.fallenMen, ...outcome.scatteredMen];
  const downed = outcome.downedCompanions.map((id) => COMPANIONS.find((c) => c.id === id)?.name ?? id);
  if (men.length === 0 && downed.length === 0) return sumRow('쓰러짐', [['없음', 'good']]);
  const parts: Part[] = [];
  if (downed.length > 0) parts.push([`부하장수 ${downed.join(', ')} 요양`, 'bad']);
  if (men.length > 0) {
    // 이름만 부르면 누가 얼마나 키운 병사였는지 모른다 — 레벨과 병종을 붙인다(플레이한 사람이 청했다).
    // 한 사람은 줄바꿈으로 갈리지 않게 묶는다.
    const rest = men.length - SUMMARY_NAMES;
    const names = el(
      'span',
      {},
      ...men.slice(0, SUMMARY_NAMES).flatMap((m, k) => [
        k > 0 ? ', ' : '',
        el('span', { class: 'sman' }, m.name, ' ', el('span', { class: 'dwho' }, whoTag(m))),
      ]),
      rest > 0 ? ` 외 ${rest}명` : '',
    );
    parts.push([`병사 ${men.length}명`, 'bad']);
    parts.push([names, '']);
  }
  return sumRow('쓰러짐', parts);
}

/** 요약의 부상 줄: 다쳐서 병영으로 돌아간 병사. 이름을 부르고(쓰러짐 줄과 같은 수까지), 이 출정은 쉰다고 적는다. */
function woundedRow(outcome: BattleOutcome): HTMLElement {
  const men = outcome.woundedMen;
  const rest = men.length - SUMMARY_NAMES;
  const names = el(
    'span',
    {},
    ...men.slice(0, SUMMARY_NAMES).flatMap((m, k) => [
      k > 0 ? ', ' : '',
      el('span', { class: 'sman' }, m.name, ' ', el('span', { class: 'dwho' }, whoTag(m))),
    ]),
    rest > 0 ? ` 외 ${rest}명` : '',
  );
  return sumRow('부상', [
    [`병사 ${men.length}명`, 'warn'],
    [names, ''],
    ['병영으로 돌아갔다 — 이 출정은 쉰다', 'dim'],
  ]);
}

/** 요약의 쓰러짐 줄이 쓰러진 병사를 빠짐없이 불렀는가. 그러면 아래 명단 칸은 되풀이다. */
function fallenListed(outcome: BattleOutcome): boolean {
  return outcome.scatteredMen.length === 0 && outcome.fallenMen.length <= SUMMARY_NAMES;
}

/**
 * 요약에서 부르는 이름 수. 넘으면 "외 N명"으로 줄이고 온 명단은 아래 칸에 둔다(전멸이면 수십 명이다).
 * 레벨·병종을 붙이며 여덟에서 여섯으로 줄였다 — 폰에서 요약이 첫 화면을 넘지 않게.
 */
const SUMMARY_NAMES = 6;

/** 이름 몇 개를 쉼표로. 넷을 넘으면 셋만 적고 나머지는 수로 — 요약 한 줄이 넘치지 않게. */
function namesOf(ids: readonly string[], name: (id: string) => string | undefined): string {
  const names = ids.map((id) => name(id) ?? id);
  return names.length > 4 ? `${names.slice(0, 3).join(', ')} 외 ${names.length - 3}` : names.join(', ');
}

/** 토벌로 오른 장수 경험치. 한도를 넘긴 몫은 적혀는 있어도 레벨에 들지 않는다. */
function raidXpText(army: Army, outcome: BattleOutcome, heroNow: number, heroBeforeRaid: number): string {
  // "+N"만 보이면 왜 안 오르는지 모른다. 이번 판에 한도를 걸쳐 넘으면 일부만 든다(7900 + 180이면
  // 100). 처음에는 시작부터 한도였을 때만 알려, 걸친 판은 +180이 다 든 것처럼 보였다(리뷰가 짚었다).
  const before = army.raidXp - outcome.raidXp;
  const counted = Math.max(0, Math.min(army.raidXp, RAID_XP_CAP) - Math.min(before, RAID_XP_CAP));
  const parts = [`장수 경험치 +${outcome.raidXp}`];
  if (counted === 0) parts.push('토벌 한도에 닿아 레벨에는 들지 않는다');
  else if (counted < outcome.raidXp) parts.push(`한도까지 ${counted}만 든다`);
  if (heroNow > heroBeforeRaid) parts.push(`레벨 ${heroBeforeRaid} → ${heroNow}`);
  return parts.join(' · ');
}

/**
 * 늘 보이는 단추 막대. 화면(#screen) 스크롤 안에서 아래에 붙는다(출정 화면의 막대와 같은 방식). 자세히를
 * 얼마나 내려 읽든 철수·진군은 손 닿는 곳에 있다.
 */
function actionBar(
  run: RunState,
  outcome: BattleOutcome,
  actions: ResultActions,
  at: {
    left: number;
    deepest: boolean;
    nextDepth: number;
    nextFresh: boolean;
    /** 다음 깊이의 적 수. 갈림길이면 길에 따른 폭("44–58"). */
    nextCount: string;
    /** 판 도중에 더 올 수 있는 적(매복·원군, `lateFoes`). */
    /** 판 도중에 더 올 수 있는 적. 갈림길이면 폭("0–16"). */
    lateCount: string;
    nextTraits: readonly string[];
  },
): HTMLElement {
  const bar = el('div', { class: 'result-actions' });
  if (run.failed) {
    // 영토 공격이었으면 지도로 간다(`main.ts`의 onReturn) — 단추 글도 그곳을 적는다(QA·리뷰가 짚었다).
    bar.append(button(run.territory ? '지도로 돌아간다' : '출정 화면으로', actions.onReturn, 'primary'));
    return bar;
  }
  if (run.territory) {
    // 영토 공격은 한 판이다. 돌아가며 전과를 확정한다(`retreat` — 최고 전과에는 들지 않는다).
    const b = button('', actions.onRetreat, 'primary');
    b.replaceChildren(el('b', {}, '지도로 돌아간다'), ' ', el('small', {}, `전과 ${outcome.meritIfRetreat.toLocaleString()} 확정`));
    bar.append(b);
    return bar;
  }
  if (run.raid) {
    // 토벌은 전과를 쌓지 않는다. 혼자 나선 사람이 확인할 값은 번 돈이다.
    bar.append(button(`돌아간다 · 번 돈 ${outcome.gold}냥`, actions.onReturn, 'primary'));
    return bar;
  }
  // 두 줄 단추: 무엇을 하는지(굵게)와 그 값(작게). 값이 단추에 붙어 있어야 막대만 보고도 고른다.
  const two = (b: HTMLButtonElement, main: string, sub: string): HTMLButtonElement => {
    b.replaceChildren(el('b', {}, main), ' ', el('small', {}, sub));
    return b;
  };
  bar.append(
    el(
      'p',
      { class: 'bar-warn' },
      at.left === 0
        ? '데려간 병력이 남지 않았다. 장수 혼자서는 더 들어갈 수 없다.'
        : at.deepest
          ? '더 들어갈 곳이 없다. 철수해 전과를 거둔다.'
          : '진군해서 장수가 쓰러지면 쌓아둔 전과가 전부 사라진다.',
    ),
    two(button('', actions.onRetreat, 'primary'), '철수하고 확정', `전과 ${outcome.meritIfRetreat}`),
  );
  if (!at.deepest) {
    bar.append(
      at.left === 0
        ? two(button('', () => {}, 'danger off'), '진군 불가', `깊이 ${at.nextDepth}`)
        : two(
            button('', actions.onAdvance, 'danger'),
            '진군',
            `깊이 ${at.nextDepth}${at.nextFresh ? ' · 첫 도전' : ''} · 적 ${at.nextCount}명${at.lateCount !== '0' ? `+${at.lateCount}` : ''}${at.nextTraits.length > 0 ? ` · ${at.nextTraits.join(' · ')}` : ''}`,
          ),
    );
  }
  return bar;
}

/** 전공 순위에 보이는 수. 넘으면 "외 N명". */
const DEEDS_SHOWN = 10;

/**
 * 전공 순위: 장수·동료·병사를 벤 수로 줄 세워 위에서 열 명. 경험치는 이번 판에 받은 총량이다 — 병사는 살아 돌아온 몫과
 * 전투 성과 몫의 합, 장수·동료는 이번 판 전과와 성과 몫의 합(철수해야 들어온다), 토벌의 장수는 토벌 경험치.
 * 쓰러진 병사도 제자리에 남는다("전사") — 벤 만큼은 그의 몫이다.
 *
 * 경험치는 수가 아니라 **오른 레벨 수**로 적는다("레벨 +0.5", `levelsGained`). 병사와 장수의 레벨 곡선이 60배쯤 달라
 * 장수 +1,351과 병사 +24가 나란히 서자 "경험치 차이 이렇게 큰게 정상이야?"라는 물음이 나왔다 — 둘 다 레벨의 절반쯤이었다.
 * 장수·동료는 앞선 판에서 쌓아 둔 몫(철수 전)까지 얹은 자리에서 잰다. 철수하면 그 자리에 이번 몫이 더해지기 때문이다.
 *
 * 처음에는 병사만 벤 순으로 모두 적고 장수·동료는 머리 줄에 수만 적었으며, 경험치는 성과 몫만 "+N"으로 붙였다.
 * 플레이한 사람이 "생존자 +28, 성과 최대 +10이 무슨 뜻이야?"라고 물은 뒤 청했다: "병사별 전공을 순위화 해서 10명만
 * 보여주자. 그리고 장수와 동료도 포함해줘. 경험치량은 총량으로 표시하자."
 *
 * 레벨이 오른 병사는 순위에서 "Lv 1 → 2▲"처럼 체력·공격의 운(▲▽)까지 적고, 따로 있던 "레벨이 오른 병사" 칸에는 순위
 * 밖의 사람만 남긴다. 두 칸이 같은 이름을 두 번 불러 결과 화면이 길었다.
 */
interface DeedRow {
  /** 병사 uid. 장수·동료는 -1. */
  readonly uid: number;
  readonly name: string;
    readonly tag: string;
    readonly kills: number;
  /** 이번 판으로 오르는 레벨 수(소수). */
  readonly gain: number;
  /** 정수 레벨의 앞뒤. `to`가 크면 "Lv 1 → 2"를 한 줄 더 적는다. */
  readonly from: number;
  readonly to: number;
  /** 철수해야 들어오는 경험치인가(장수·동료). */
  readonly later: boolean;
  readonly fate: string;
  readonly hero: boolean;
}

export interface Ranking {
  /** 위에서 `DEEDS_SHOWN`명. */
  readonly shown: readonly DeedRow[];
  /** 그 아래 사람 수. */
  readonly rest: number;
}

/** 전공 순위를 줄 세운다. 적을 벤 사람만 든다 — 아무도 베지 않았으면 null. 순위 칸과 "순위 밖에서 레벨이 오른 병사" 칸이 함께 쓴다. */
export function rankDeeds(army: Army, run: RunState, outcome: BattleOutcome): Ranking | null {
  const raid = run.raid;
  const rows: DeedRow[] = [];
  const gains = heroLevelGains(army, run, outcome);
  rows.push({ uid: -1, name: '장수', tag: '', kills: outcome.leaderKills, ...gains.leader, later: !raid, fate: outcome.leaderAlive ? '' : '쓰러짐', hero: true });
  outcome.companionDeeds.forEach((c, k) => {
    rows.push({
      uid: -1,
      name: c.name,
      tag: c.escort ? '부관' : '부하장수',
      kills: c.kills,
      ...gains.companions[k],
      later: true,
      fate: c.downed ? '요양' : '',
      hero: true,
    });
  });
  for (const d of outcome.deeds) {
    rows.push({
      uid: d.uid,
      name: d.name,
      tag: whoTag(d),
      kills: d.kills,
      gain: d.levelGain,
      from: d.level,
      to: d.levelTo,
      later: false,
      fate: d.fate === 'fell' ? '전사' : d.fate === 'lost' ? '낙오' : d.fate === 'hurt' ? '부상' : '',
      hero: false,
    });
  }
  // 벤 적이 없는 사람은 순위에 넣지 않는다 — "벤 0"이 줄을 차지하면 전공 순위가 명부처럼 길어진다(플레이한 사람:
  // "전공 순위에 벤 적이 없을 때는 포함시키지 말자"). 그 가운데 레벨이 오른 병사는 "순위 밖에서 레벨이 오른 병사"
  // 칸이 부른다(`levelUpsOutsideRanking`). 아무도 베지 않았으면 순위 칸이 없다.
  const scored = rows.map((r, k) => ({ r, k })).filter((x) => x.r.kills > 0);
  if (scored.length === 0) return null;
  // 벤 수, 같으면 오른 레벨 순. 그래도 같으면 넣은 순서(장수 → 동료 → 병사, 병사는 뽑힌 순)로 — 늘 같은 줄.
  const ranked = scored
    .sort((a, b) => b.r.kills - a.r.kills || b.r.gain - a.r.gain || a.k - b.k)
    .map((x) => x.r);
  const shown = ranked.slice(0, DEEDS_SHOWN);
  return { shown, rest: ranked.length - shown.length };
}

/** 레벨이 오른 병사 중 순위에 나오지 않은 사람. 순위가 없으면 모두. 순위 줄이 이미 부른 사람을 또 부르지 않는다. */
export function levelUpsOutsideRanking(ranking: Ranking | null, ups: readonly LevelUp[]): LevelUp[] {
  return ups.filter((u) => !ranking?.shown.some((r) => r.uid === u.uid));
}

function deedsPanel(ranking: Ranking | null, outcome: BattleOutcome): HTMLElement | null {
  if (!ranking) return null;
  const { shown, rest } = ranking;
  // 레벨이 오른 병사는 그 레벨에서 받은 체력·공격의 운(▲▽)을 함께 적는다. 따로 있던 "레벨이 오른 병사" 칸을 순위에 합쳤다.
  const luckOf = (r: DeedRow): string => {
    const u = r.uid >= 0 ? outcome.levelUps.find((x) => x.uid === r.uid) : undefined;
    return u ? luck(u) : '';
  };
  const marked = outcome.levelUps.some((u) => luck(u) !== '');
  return el(
    'div',
    { class: 'panel deeds' },
    el('h3', {}, '전공 순위'),
    el(
      'ol',
      { class: 'deed-rank' },
      ...shown.map((r, k) =>
        el(
          'li',
          { class: `drow${r.hero ? ' hero' : ''}${r.fate ? ' gone' : ''}` },
          el('span', { class: 'drank' }, `${k + 1}`),
          el(
            'span',
            { class: 'dwho-cell' },
            el('span', { class: 'dname' }, r.name),
            r.tag ? ' ' : '',
            r.tag ? el('span', { class: 'dwho' }, r.tag) : null,
            r.fate ? ' ' : '',
            // 다친 병사는 잃은 병사(전사·낙오)와 다른 색으로 적는다 — 같은 붉은색이면 한눈에 갈리지 않았다(QA).
            r.fate ? el('span', { class: r.fate === '부상' ? 'dfate hurt' : 'dfate' }, r.fate) : null,
          ),
          el('span', { class: 'dkills' }, `벤 ${r.kills}`),
          el(
            'span',
            { class: 'dxp' },
            r.gain > 0 ? `레벨${levelText(r.gain)}${r.later ? '*' : ''}` : '—',
            // 소수만으로는 "올랐다"가 잘 느껴지지 않는다. 정수 레벨이 바뀐 사람만 앞뒤 레벨을 한 줄 더 적는다.
            r.to > r.from ? el('span', { class: 'dlvup' }, `Lv ${r.from} → ${r.to}${luckOf(r)}`) : null,
          ),
        ),
      ),
    ),
    el(
      'p',
      // 낱말 단위로 줄을 넘긴다(`keep`) — '부하장수'로 길어지며 360px에서 '부하 / 장수'로 끊겼다(QA).
      { class: 'dim keep' },
      [
        rest > 0 ? `외 ${rest}명.` : '',
        '오른 레벨을 적는다 — 레벨마다 필요한 경험치가 달라서다.',
        // "*" 풀이는 그 표가 붙은 줄이 있을 때만. 벤 적 없는 장수·동료는 순위에 들지 않으니 없을 수 있다.
        shown.some((r) => r.later && r.gain > 0) ? '* 장수·부하장수의 몫은 철수해야 들어온다. 부하장수가 그 전에 쓰러지면 받지 못한다.' : '',
        marked ? LUCK_NOTE : '',
      ]
        .filter(Boolean)
        .join(' '),
    ),
  );
}

/**
 * 오른 레벨 수. 한 자리까지 적되, 0.1이 안 되면 두 자리 — "+0.0"이면 아무것도 못 받은 것처럼 읽힌다. 0.01도 안 되면
 * "미량". 처음에는 0.01로 올려 적어, 토벌 한도에 걸려 0.0014 오른 장수가 "+0.01"로 부풀려 보였다(리뷰가 짚었다).
 * "레벨" 바로 뒤에 붙으므로 앞에 한 칸을 둔다 — 빠뜨려 "레벨+1.2"로 붙어 나온 적이 있다(QA가 짚었다).
 */
function levelText(gain: number): string {
  if (gain >= 0.1) return ` +${gain.toFixed(1)}`;
  if (gain >= 0.01) return ` +${gain.toFixed(2)}`;
  return ' 미량';
}

/**
 * 레벨이 오른 병사. 살아 돌아온 것이 쌓여 사람이 자라는 것이 보이는 자리다 — 쓰러진
 * 병사의 이름만 부르고 자란 병사는 숫자로 넘기면, 잃는 쪽만 무겁고 키우는 쪽은 안 보인다.
 */
function levelUpPanel(ups: readonly LevelUp[], ranked: boolean): HTMLElement | null {
  if (ups.length === 0) return null;
  const shown = ups
    .slice(0, FALLEN_SHOWN)
    .map((u) => `${u.name} ${u.level}${luck(u)}`)
    .join(' · ');
  const rest = ups.length - FALLEN_SHOWN;
  return el(
    'div',
    { class: 'panel leveled' },
    el('h3', {}, `${ranked ? '순위 밖에서 ' : ''}레벨이 오른 병사 ${ups.length}명`),
    el('p', { class: 'fallen-names' }, rest > 0 ? `${shown} 외 ${rest}명` : shown),
    // 순위가 있으면 ▲▽ 풀이는 순위 각주가 맡는다(이 칸은 그 바로 아래다). 같은 문장을 두 번 두지 않는다.
    ranked ? null : el('p', { class: 'dim' }, LUCK_NOTE),
  );
}

/** 레벨마다 굴린 체력·공격 몫이 보통보다 많으면 ▲, 적으면 ▽. 숫자를 다 적으면 스무 명 줄이 읽히지 않는다. */
function luck(u: LevelUp): string {
  const d = u.hp - u.usualHp + (u.atk - u.usualAtk);
  return d > 0 ? '▲' : d < 0 ? '▽' : '';
}

const LUCK_NOTE = '레벨마다 오르는 체력·공격은 병사마다 조금씩 다르다. ▲는 보통보다 많이, ▽는 적게 받았다. 병사 카드를 누르면 쌓인 몫이 보인다.';

/** 이 판에 이름을 부를 수 있는 병사가 몇이 넘으면 줄인다. 전멸한 판은 수십 명이다. */
const FALLEN_SHOWN = 24;

/**
 * 쓰러진 병사의 이름. 숫자로만 셀 때 잃는 것은 재건 비용이었다. 이름이 붙으면 누가
 * 쓰러졌는지가 된다 — 병사를 작고 무겁게 가기로 한 까닭이 여기서 보인다.
 */
function fallenPanel(label: string, men: readonly Who[]): HTMLElement | null {
  if (men.length === 0) return null;
  const rest = men.length - FALLEN_SHOWN;
  return el(
    'div',
    { class: 'panel fallen' },
    el('h3', {}, `${label} ${men.length}명`),
    // 목록으로 둔다. 처음에는 한 문단에 <span>을 이어 붙여 CSS 간격으로만 나눴더니, 화면 읽기로는
    // "온흘지Lv1 부월수미별우Lv2 부월수"처럼 경계가 사라졌다(리뷰가 짚었다). 이름과 꼬리표 사이의
    // 빈칸도 같은 까닭이다 — 플렉스가 화면에서는 지우고 읽기에는 남긴다.
    el(
      'ul',
      { class: 'fallen-names fallen-men' },
      ...men.slice(0, FALLEN_SHOWN).map((m) =>
        el('li', { class: 'fman' }, m.name, ' ', el('span', { class: 'dwho' }, whoTag(m))),
      ),
      rest > 0 && el('li', { class: 'fman' }, `외 ${rest}명`),
    ),
  );
}

/** "Lv3 창수". 티어는 병종 이름이 이미 말한다(민병과 숙련 창수는 이름이 다르다). */
function whoTag(w: Who): string {
  return `Lv${w.level} ${TROOP_BY_ID.get(w.troop)?.name ?? w.troop}`;
}

function lostSlotText(outcome: BattleOutcome): string {
  const slots = outcome.gearLost.flatMap((g) => g.slots.map((s) => SLOT_NAMES[s]));
  return slots.join('·');
}

function stat(label: string, value: string, cls = ''): HTMLElement {
  return el(
    'div',
    { class: `stat ${cls}` },
    el('span', { class: 'k' }, label),
    el('span', { class: 'v' }, value),
  );
}
